/**
 * Rueckmeldung beim Antippen und Anklicken.
 *
 * Chrome unter Android legt ueber alles Antippbare kurz eine blaue Flaeche
 * (tap highlight). Die ist im Stilblatt abgeschaltet; an ihre Stelle tritt
 * diese Rueckmeldung: was getroffen wurde, faerbt sich kurz grau.
 *
 * - Flaechen mit eigenem Grund (Knoepfe, Pillen, Kreuze in ihrer Blase)
 *   bekommen einen grauen Schleier ueber ihren Grund.
 * - Fotos (Kacheln, Galerie, Vorschauen) verlieren Kontrast und Farbe - grau.
 * - Schrift und Zeichen ohne eigenen Grund (Verweise, Menue, Symbole in der
 *   Kopfzeile, das Logo) werden grau.
 *
 * Fuehrt der Klick auf eine neue Seite, bleibt die Markierung stehen, bis
 * die Seite wechselt - man sieht, dass der Klick angekommen ist. Oeffnet er
 * nur ein Fenster oder tut etwas auf der Seite, kehrt sie nach einem
 * Augenblick in den Ausgangszustand zurueck.
 *
 * Bewusst nie ueber filter oder die Deckkraft eines Behaelters: beides
 * machte ihn zum Bezugsrahmen fuer das Milchglas darin, und die Pillen auf
 * einer Kachel verloren fuer die Dauer ihre Weichzeichnung. Gefiltert
 * werden nur Bilder selbst - sie haben keine Kinder.
 *
 * Mit dem Finger setzt die Markierung einen Wimpernschlag verzoegert ein:
 * wer eine Kachel nur beruehrt, um die Seite zu rollen, soll sie nicht
 * aufleuchten sehen.
 */

const ZIELE = [
  'a[href]',
  'button',
  'summary',
  'label',
  'select',
  '[role="button"]',
  '[role="tab"]',
  '[role="option"]',
  '[role="menuitem"]',
  '[role="link"]',
  'input[type="checkbox"]',
  'input[type="radio"]',
  'input[type="submit"]',
  'input[type="button"]',
].join(',');

/** Felder, in die getippt wird - dort wird nichts markiert. */
const SCHREIBFELD =
  'input:not([type]), input[type="text"], input[type="search"], input[type="email"], input[type="tel"], ' +
  'input[type="url"], input[type="number"], input[type="password"], input[type="date"], textarea, [contenteditable=""], [contenteditable="true"]';

/** Wartezeit mit dem Finger, bis die Markierung erscheint (ms). */
const VERZUG = 60;
/** Wie weit sich der Finger bewegen darf, bevor es als Wischen gilt (px). */
const SPIELRAUM = 8;
/** Dauer des Aufleuchtens und der Rueckkehr (ms). */
const HINEIN = 80;
const ZURUECK = 260;
/** So lange steht die Markierung mindestens, bevor sie zurueckkehrt (ms). */
const HALTEN = 180;
/** Laedt die neue Seite nicht, kehrt die Markierung spaetestens dann zurueck (ms). */
const HOECHSTENS = 8000;

/** Das Grau der Markierung - auf hellem wie auf dunklem Grund sichtbar. */
const GRAU = 'rgb(138 138 138)';
const SCHLEIER = 'rgb(128 128 128 / 0.34)';
/*
 * Auf hellen Flaechen (weisse Knoepfe) waere derselbe Schleier kaum mehr als
 * ein Hauch - dort liegt er kraeftiger, damit der Druck ebenso sichtbar ist.
 */
const SCHLEIER_HELL = 'rgb(128 128 128 / 0.55)';
const OHNE_SCHLEIER = 'rgb(128 128 128 / 0)';
// Weniger Kontrast und Farbe zieht ein Foto zum Grau hin - bei hellen wie dunklen Bildern.
const FOTO_AUS = 'contrast(0.6) saturate(0.45) brightness(0.96)';
const FOTO_AN = 'contrast(1) saturate(1) brightness(1)';

/** Ab dieser Kantenlaenge ist ein Bild ein Foto, darunter ein Zeichen (Logo, Symbol). */
const FOTO_AB = 48;

/**
 * Das Element, das auf den Druck antworten soll - auch durch Schattenbaeume
 * hindurch (das Konto-Fenster liegt in einem). Zuerst zaehlen die
 * bedienbaren Elemente; findet sich keines, das Element, das den
 * Hand-Zeiger traegt (eigene Klickflaechen wie die Bilder der Galerie).
 *
 * @param {EventTarget[]} pfad
 * @returns {HTMLElement | null}
 */
export function zielAusPfad(pfad) {
  const elemente = pfad.filter((knoten) => knoten instanceof Element);
  for (const knoten of elemente) {
    if (knoten.matches(SCHREIBFELD)) return null;
    /*
     * Ausgenommen die Buehne des Anmalens (SOLID): Dort liegt die Leinwand, auf der gemalt wird - nur das Schliessen-X traegt
     * wie jedes andere die graue Tipp-Markierung (Jimmi, Oktober 2026).
     */
    const sperre = knoten.closest('[data-dp-kein-druck]');
    if (sperre && !knoten.closest('.dp-anmalen__buehne .dp-bildeditor__zu')) return null;
    if (!knoten.matches(ZIELE)) continue;
    if (knoten.matches(':disabled, [aria-disabled="true"]')) return null;
    // Eine Beschriftung, die ein Schreibfeld traegt, ist dessen Rahmen.
    if (knoten.tagName === 'LABEL' && knoten.querySelector(SCHREIBFELD)) return null;
    return /** @type {HTMLElement} */ (knoten);
  }

  /*
   * Der Hand-Zeiger vererbt sich - gemeint ist das Element, bei dem er
   * beginnt: das hoechste mit Hand, dessen Eltern sie nicht haben.
   */
  for (const knoten of elemente) {
    if (!(knoten instanceof HTMLElement) && !(knoten instanceof SVGElement)) continue;
    if (getComputedStyle(knoten).cursor !== 'pointer') continue;
    let anfang = knoten;
    while (anfang.parentElement && getComputedStyle(anfang.parentElement).cursor === 'pointer') {
      anfang = anfang.parentElement;
    }
    if (anfang === document.body || anfang === document.documentElement) return null;
    return /** @type {HTMLElement} */ (anfang);
  }
  return null;
}

/** @param {string} farbe */
function sichtbar(farbe) {
  if (!farbe || farbe === 'transparent') return false;
  const alpha = farbe.match(/rgba?\([^)]*[,/]\s*([\d.]+)\s*\)/);
  return !alpha || Number(alpha[1]) > 0.02;
}

/** Alle Schliessen-Kreuze der Fenster - dieselbe Liste wie im Stilblatt ("Das Schliessen-X"). */
const SCHLIESSEN_X = [
  '.theme-drawer__close-button',
  '.menu-drawer__close-button',
  '.predictive-search__close-modal-button',
  '.dp-widerruf__zu',
  '.dp-ib__zu',
  '.dp-korb-perso-zu',
  '.dp-bildfeld__zu',
  '.dp-bildfeld__knopf',
  '.dp-lupe__zu',
  '.dp-cookie-zu',
  '#shopify-pc__prefs__header-close',
  '.account__close',
].join(',');

/** Ist der eigene Grund hell (weisser Knopf)? Gerechnet mit der Deckung. */
function istHell(element) {
  const teile = getComputedStyle(element).backgroundColor.match(/[\d.]+/g);
  if (!teile || teile.length < 3) return false;
  const [r, g, b] = teile.map(Number);
  const deckung = teile.length > 3 ? Number(teile[3]) : 1;
  return deckung > 0.5 && (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255 > 0.7;
}

/** Traegt das Element einen eigenen Grund - Farbe, Verlauf oder Milchglas? */
function hatGrund(element) {
  const stil = getComputedStyle(element);
  if (sichtbar(stil.backgroundColor)) return true;
  if (stil.backgroundImage && stil.backgroundImage !== 'none') return true;
  const glas = stil.backdropFilter || stil.webkitBackdropFilter;
  return !!glas && glas !== 'none';
}

/**
 * Traegt das Element selbst sichtbaren Text? Text nur fuer Vorleseprogramme
 * (visually-hidden, auf einen Pixel geschrumpft) zaehlt nicht - so ein
 * Verweis liegt meist unsichtbar ueber einer ganzen Kachel.
 */
function hatSichtbarenText(element) {
  // Die Huelle zaehlt mit: bei visually-hidden laeuft der Text ueber einen Pixel hinaus.
  const huelle = element.getBoundingClientRect();
  if (huelle.width <= 2 || huelle.height <= 2) return false;
  for (const kind of element.childNodes) {
    if (kind.nodeType !== Node.TEXT_NODE || !kind.textContent.trim()) continue;
    const bereich = document.createRange();
    bereich.selectNodeContents(kind);
    const rand = bereich.getBoundingClientRect();
    if (rand.width > 2 && rand.height > 2) return true;
  }
  return false;
}

/**
 * Die Flaechen mit eigenem Grund innerhalb eines Elements - etwa das
 * Farbfeld in der Beschriftung einer Variante. Nur sichtbare, hoechstens
 * sechs, und keine, die in einer anderen gefundenen stecken.
 *
 * @param {Element} element
 * @returns {HTMLElement[]}
 */
function innereFlaechen(element) {
  /** @type {HTMLElement[]} */
  const gefunden = [];
  const kinder = element.querySelectorAll('*');
  for (let i = 0; i < kinder.length && i < 60 && gefunden.length < 6; i += 1) {
    const kind = kinder[i];
    if (!(kind instanceof HTMLElement) || gefunden.some((f) => f.contains(kind))) continue;
    const rand = kind.getBoundingClientRect();
    if (rand.width <= 2 || rand.height <= 2 || !hatGrund(kind)) continue;
    gefunden.push(kind);
  }
  return gefunden;
}

/** Zeigt das Element selbst etwas - Schrift, Bild, Zeichen oder Grund? */
function zeigtEtwas(element) {
  if (hatGrund(element)) return true;
  if (innereFlaechen(element).length) return true;
  if (element.matches('img, svg, video, canvas, picture')) return true;
  if (element.querySelector('img, svg, video, canvas, picture')) return true;
  return [element, ...element.querySelectorAll('*')].some(hatSichtbarenText);
}

/**
 * Die Flaeche, die markiert wird. Meist das getroffene Element selbst; ein
 * unsichtbarer Verweis, der ueber einer Kachel liegt (die ganze Karte ist
 * anklickbar), zeigt aber nichts - dann gilt die Kachel darunter.
 *
 * @param {HTMLElement} ziel
 */
export function flaecheVon(ziel) {
  let flaeche = ziel;
  for (let stufe = 0; stufe < 3 && !zeigtEtwas(flaeche) && flaeche.parentElement; stufe += 1) {
    flaeche = flaeche.parentElement;
  }

  /*
   * Umgekehrt: ein Verweis ohne eigenen Grund, der eine einzige Blase
   * umschliesst ("Mehr anzeigen"), meint die Blase - sie bekommt den
   * Schleier wie jeder Knopf.
   */
  for (let stufe = 0; stufe < 2 && !hatGrund(flaeche) && flaeche.children.length === 1; stufe += 1) {
    const kind = /** @type {HTMLElement} */ (flaeche.children[0]);
    const aussen = flaeche.getBoundingClientRect();
    const innen = kind.getBoundingClientRect();
    if (innen.width * innen.height < aussen.width * aussen.height * 0.7) break;
    if (!hatGrund(kind) && kind.children.length !== 1) break;
    flaeche = kind;
  }
  return flaeche;
}

/** Liegt das Element sichtbar im Bild? Ausgeblendete (etwa das zweite Logo) zaehlen nicht. */
function imBild(element) {
  const stil = getComputedStyle(element);
  if (stil.visibility === 'hidden' || Number(stil.opacity) === 0) return false;
  const rand = element.getBoundingClientRect();
  if (rand.width < 1 || rand.height < 1) return false;
  return rand.bottom > 0 && rand.right > 0 && rand.top < window.innerHeight && rand.left < window.innerWidth;
}

/**
 * Was bei einer Flaeche wie grau wird - als Liste von Element und
 * Ausgangs- und Zielbild der Animation.
 *
 * @param {HTMLElement} ziel
 * @returns {{ element: Element, ruhe: Keyframe, druck: Keyframe }[]}
 */
export function wirkungenFuer(ziel) {
  const flaeche = flaecheVon(ziel);
  const rand = flaeche.getBoundingClientRect();
  if (!rand.width || !rand.height) return [];
  const riesig = rand.width > window.innerWidth * 0.9 && rand.height > window.innerHeight * 0.6;
  /** @type {{ element: Element, ruhe: Keyframe, druck: Keyframe }[]} */
  const wirkungen = [];

  // Der graue Schleier ueber dem eigenen Grund - nicht ueber ganzen Seitenflaechen.
  const schleier = !riesig && hatGrund(flaeche);
  if (schleier) {
    const schatten = getComputedStyle(flaeche).boxShadow;
    const davor = schatten && schatten !== 'none' ? schatten + ', ' : '';
    wirkungen.push({
      element: flaeche,
      ruhe: { boxShadow: davor + 'inset 0 0 0 100vmax ' + OHNE_SCHLEIER },
      druck: { boxShadow: davor + 'inset 0 0 0 100vmax ' + (istHell(flaeche) ? SCHLEIER_HELL : SCHLEIER) },
    });
  }

  /*
   * Das Schliessen-X leuchtet wie das Vorbild im Warenkorb auf: seine Flaeche
   * geht von 8 auf 18 Prozent (--dp-x-deckung, divineprint.css "Das
   * Schliessen-X"). Ueber die Markierung statt ueber :active, damit es bei
   * jedem X gleich lang zu sehen ist - auch dort, wo das Fenster sofort
   * schliesst.
   */
  if (flaeche.matches(SCHLIESSEN_X)) {
    wirkungen.push({ element: flaeche, ruhe: { '--dp-x-deckung': '0.08' }, druck: { '--dp-x-deckung': '0.18' } });
  }

  /*
   * Ohne eigenen Grund, aber mit Flaechen darin (Farbfeld einer Variante,
   * Blase in einem Verweis): die bekommen den Schleier.
   */
  if (!schleier && !riesig) {
    for (const innen of innereFlaechen(flaeche)) {
      if (!imBild(innen)) continue;
      const schatten = getComputedStyle(innen).boxShadow;
      const davor = schatten && schatten !== 'none' ? schatten + ', ' : '';
      wirkungen.push({
        element: innen,
        ruhe: { boxShadow: davor + 'inset 0 0 0 100vmax ' + OHNE_SCHLEIER },
        druck: { boxShadow: davor + 'inset 0 0 0 100vmax ' + SCHLEIER },
      });
    }
  }

  // Bilder: Fotos werden grauer, kleine Zeichen (Logo, Symbole) blasser.
  const bilder = [
    ...(flaeche.matches('img, video, canvas, svg') ? [flaeche] : []),
    ...flaeche.querySelectorAll('img, video, canvas, svg'),
  ].filter(imBild);
  let fotos = 0;
  for (const bild of bilder) {
    // Zeichnungen innerhalb einer Zeichnung zaehlen nicht einzeln.
    if (bild instanceof SVGElement && bild.parentElement && bild.parentElement.closest('svg')) continue;
    const masse = bild.getBoundingClientRect();
    const foto = !(bild instanceof SVGElement) && masse.width >= FOTO_AB && masse.height >= FOTO_AB;
    if (foto) {
      if (fotos >= 8) continue;
      fotos += 1;
      const filter = getComputedStyle(bild).filter;
      const eigener = filter && filter !== 'none' ? filter + ' ' : '';
      wirkungen.push({ element: bild, ruhe: { filter: eigener + FOTO_AN }, druck: { filter: eigener + FOTO_AUS } });
    } else if (!schleier) {
      /*
       * Zeichen ohne eigenen Grund: Symbole mit currentColor faerben sich
       * mit der Schrift unten grau; fest gefaerbte (das Logo) werden blasser -
       * auf hellem wie auf dunklem Grund sieht das grau aus.
       */
      const deckkraft = Number(getComputedStyle(bild).opacity);
      wirkungen.push({ element: bild, ruhe: { opacity: String(deckkraft) }, druck: { opacity: String(deckkraft * 0.45) } });
    }
  }

  /*
   * Schrift wird grau - an jedem Element, das selbst Text traegt. Nicht,
   * wo der Schleier schon liegt: dort genuegt er.
   */
  if (!schleier) {
    const traeger = [flaeche, ...flaeche.querySelectorAll('*')].filter(hatSichtbarenText);
    for (const element of traeger.slice(0, 24)) {
      if (!imBild(element)) continue;
      wirkungen.push({ element, ruhe: { color: getComputedStyle(element).color }, druck: { color: GRAU } });
    }
  }

  return wirkungen;
}

/* ------------------------------------------------------------ Der Ablauf */

/**
 * @typedef {{
 *   ziel: HTMLElement,
 *   x: number, y: number, id: number, sicht: number,
 *   uhr: number,
 *   laeufe: { element: Element, lauf: Animation, ruhe: Keyframe, druck: Keyframe }[],
 *   an: boolean,
 *   seit: number,
 *   haelt: boolean,
 *   rueckUhr: number,
 * }} Druck
 */

/** Die Hoehe des sichtbaren Bereichs - sie aendert sich, wenn die Tastatur auf- oder zugeht. */
function sichtHoehe() {
  return window.visualViewport ? window.visualViewport.height : window.innerHeight;
}

/** @type {Druck | null} */
let druck = null;
/** Rueckkehr-Animationen, die gerade laufen. @type {Set<Animation>} */
const rueckkehrer = new Set();
/** Markierungen, die bis zum Seitenwechsel stehen bleiben. @type {Druck[]} */
const stehende = [];

/** @param {Druck} eintrag */
function leuchte(eintrag) {
  if (eintrag.an) return;
  eintrag.an = true;
  eintrag.seit = performance.now();
  /*
   * Kehrt eine vorige Markierung gerade noch zurueck, springt sie ans Ende -
   * sonst laese die neue deren Zwischenstand als Ausgangslage.
   */
  for (const lauf of rueckkehrer) lauf.finish();
  for (const wirkung of wirkungenFuer(eintrag.ziel)) {
    const lauf = wirkung.element.animate([wirkung.ruhe, wirkung.druck], {
      duration: HINEIN,
      easing: 'ease-out',
      fill: 'forwards',
    });
    eintrag.laeufe.push({ ...wirkung, lauf });
  }
}

/**
 * Laesst die Markierung zurueckkehren - erst, wenn sie ganz zu sehen war.
 *
 * @param {Druck} eintrag
 * @param {boolean} [sofort]
 */
function erlischt(eintrag, sofort = false) {
  window.clearTimeout(eintrag.uhr);
  window.clearTimeout(eintrag.rueckUhr);
  const laeufe = eintrag.laeufe;
  eintrag.laeufe = [];
  if (sofort) {
    for (const { lauf } of laeufe) lauf.cancel();
    return;
  }
  const warten = Math.max(0, HINEIN - (performance.now() - eintrag.seit));
  window.setTimeout(() => {
    for (const { element, lauf, ruhe, druck: bild } of laeufe) {
      const zurueck = element.animate([bild, ruhe], { duration: ZURUECK, easing: 'ease-out' });
      lauf.cancel();
      rueckkehrer.add(zurueck);
      const fertig = () => rueckkehrer.delete(zurueck);
      zurueck.finished.then(fertig, fertig);
    }
  }, warten);
}

/** Beendet den laufenden Druck (Wischen, Abbruch) - ohne ihn zu zeigen. */
function brichAb() {
  if (!druck) return;
  const jetzt = druck;
  druck = null;
  erlischt(jetzt);
}

document.addEventListener(
  'pointerdown',
  (ereignis) => {
    if (!ereignis.isPrimary || (ereignis.pointerType === 'mouse' && ereignis.button !== 0)) return;
    brichAb();
    const ziel = zielAusPfad(ereignis.composedPath());
    if (!ziel) return;
    druck = {
      ziel,
      x: ereignis.clientX,
      y: ereignis.clientY,
      sicht: sichtHoehe(),
      id: ereignis.pointerId,
      uhr: 0,
      laeufe: [],
      an: false,
      seit: 0,
      haelt: false,
      rueckUhr: 0,
    };
    /*
     * Mit der Maus sofort - mit dem Finger erst, wenn es kein Rollen ist.
     * Knoepfe (bis 64 Pixel hoch, auch breite wie "Artikel laden") und
     * kleine Flaechen leuchten auch mit dem Finger sofort: auf ihnen beginnt kaum jemand zu rollen, und viele loesen
     * gleich etwas aus (Fenster zu, Seite weiter) - mit Verzug waere die
     * Markierung dann schon wieder weg, bevor man sie sieht.
     */
    const rand = ziel.getBoundingClientRect();
    const knopf = ziel.matches('button, [role="button"], summary, select, input, label') && rand.height <= 64;
    if (ereignis.pointerType === 'mouse' || knopf || (rand.width <= 140 && rand.height <= 64) || ziel.matches(SCHLIESSEN_X)) leuchte(druck);
    else {
      const eintrag = druck;
      eintrag.uhr = window.setTimeout(() => leuchte(eintrag), VERZUG);
    }
  },
  { passive: true, capture: true }
);

document.addEventListener(
  'pointermove',
  (ereignis) => {
    if (!druck || ereignis.pointerId !== druck.id || ereignis.pointerType === 'mouse') return;
    /*
     * Schliesst sich unter dem Finger die Tastatur (erst getippt, dann aufs
     * Bild), verschiebt sich der sichtbare Bereich - die Zahlen des Zeigers
     * wandern mit, ohne dass der Finger sich bewegt. Das ist kein Wischen.
     */
    if (Math.abs(sichtHoehe() - druck.sicht) > 40) return;
    if (Math.hypot(ereignis.clientX - druck.x, ereignis.clientY - druck.y) > SPIELRAUM) brichAb();
  },
  { passive: true, capture: true }
);

document.addEventListener(
  'pointerup',
  (ereignis) => {
    if (!druck || ereignis.pointerId !== druck.id) return;
    const eintrag = druck;
    window.clearTimeout(eintrag.uhr);
    // Ein kurzer Tipp zeigt die Markierung trotzdem.
    leuchte(eintrag);
    /*
     * Der Klick folgt gleich - fuehrt er auf eine neue Seite, haelt er die
     * Markierung fest (siehe unten). Sonst kehrt sie nach kurzem zurueck.
     */
    eintrag.rueckUhr = window.setTimeout(() => {
      if (druck === eintrag) druck = null;
      if (!eintrag.haelt) erlischt(eintrag);
    }, HALTEN);
  },
  { passive: true, capture: true }
);

document.addEventListener(
  'pointercancel',
  (ereignis) => {
    if (druck && ereignis.pointerId === druck.id) brichAb();
  },
  { passive: true, capture: true }
);

/* ------------------------------------------ Fuehrt der Klick fort? */

/**
 * Wird der Klick die Seite wechseln? Ein Verweis auf eine andere Adresse,
 * im selben Reiter, ohne dass ein Skript ihn abgefangen hat.
 *
 * @param {MouseEvent} ereignis
 */
function fuehrtFort(ereignis) {
  if (ereignis.defaultPrevented) return false;
  if (ereignis.metaKey || ereignis.ctrlKey || ereignis.shiftKey || ereignis.altKey) return false;
  const verweis = ereignis
    .composedPath()
    .find((knoten) => knoten instanceof HTMLAnchorElement && knoten.hasAttribute('href'));
  if (!(verweis instanceof HTMLAnchorElement)) return false;
  if ((verweis.target && verweis.target !== '_self') || verweis.hasAttribute('download')) return false;
  let adresse;
  try {
    adresse = new URL(verweis.href, window.location.href);
  } catch {
    return false;
  }
  if (adresse.protocol !== 'http:' && adresse.protocol !== 'https:') return false;
  const hier = window.location;
  // Nur eine andere Marke auf derselben Seite laedt nichts neu.
  if (adresse.origin === hier.origin && adresse.pathname === hier.pathname && adresse.search === hier.search) {
    return false;
  }
  return true;
}

/** @param {Druck} eintrag */
function halteFest(eintrag) {
  eintrag.haelt = true;
  window.clearTimeout(eintrag.rueckUhr);
  stehende.push(eintrag);
  // Kommt die neue Seite nicht, kehrt die Markierung trotzdem zurueck.
  eintrag.rueckUhr = window.setTimeout(() => {
    const stelle = stehende.indexOf(eintrag);
    if (stelle >= 0) stehende.splice(stelle, 1);
    erlischt(eintrag);
  }, HOECHSTENS);
}

/*
 * Nach allen anderen Hoerern (Blase am Fenster): erst dann steht fest, ob
 * ein Skript den Klick abgefangen hat.
 */
window.addEventListener('click', (ereignis) => {
  const eintrag = druck;
  if (!eintrag || !eintrag.an) return;
  if (!ereignis.composedPath().includes(eintrag.ziel)) return;
  if (fuehrtFort(ereignis)) halteFest(eintrag);
});

// Ein Formular, das abgeschickt wird und nicht abgefangen ist, laedt ebenfalls neu.
window.addEventListener('submit', (ereignis) => {
  const eintrag = druck;
  if (!eintrag || !eintrag.an || ereignis.defaultPrevented) return;
  const formular = ereignis.target;
  if (!(formular instanceof HTMLFormElement) || (formular.target && formular.target !== '_self')) return;
  if (formular.contains(eintrag.ziel) || ereignis.submitter === eintrag.ziel) halteFest(eintrag);
});

/*
 * Zurueck mit der Zuruecktaste: der Browser stellt die Seite aus seinem
 * Zwischenspeicher wieder her - samt stehender Markierung. Sie geht dann
 * ohne Umweg weg.
 */
window.addEventListener('pageshow', (ereignis) => {
  if (!ereignis.persisted) return;
  while (stehende.length) erlischt(/** @type {Druck} */ (stehende.pop()), true);
  if (druck) {
    erlischt(druck, true);
    druck = null;
  }
});
